import React, { useState, useEffect } from 'react'
import { X, Calendar, User, FileText, Clock, ChevronLeft, ChevronRight } from 'lucide-react'

const RevisionHistoryModal = ({ isOpen, onClose, revisionHistory, sacCode }) => {
  const [currentRevisionIndex, setCurrentRevisionIndex] = useState(0)
  
  useEffect(() => {
    if (revisionHistory && revisionHistory.length > 0) {
      // Start with the latest revision (index 0)
      setCurrentRevisionIndex(0)
    }
  }, [revisionHistory])

  if (!isOpen || !revisionHistory || revisionHistory.length === 0) return null

  const currentRevision = revisionHistory[currentRevisionIndex]
  const totalRevisions = revisionHistory.length
  const isFirstRevision = currentRevisionIndex === totalRevisions - 1
  const isLastRevision = currentRevisionIndex === 0

  const handlePrevious = () => {
    if (currentRevisionIndex < totalRevisions - 1) {
      setCurrentRevisionIndex(currentRevisionIndex + 1)
    }
  }

  const handleNext = () => {
    if (currentRevisionIndex > 0) {
      setCurrentRevisionIndex(currentRevisionIndex - 1)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="revision-history-modal-overlay">
      <div className="revision-history-modal">
        <div className="revision-history-modal-header">
          <div className="revision-header-left">
            <h3>Revision History - {sacCode}</h3>
            <div className="revision-navigation">
              <button 
                className="nav-button" 
                onClick={handlePrevious}
                disabled={isFirstRevision}
                title="Previous Revision"
              >
                <ChevronLeft size={16} />
              </button>
              <span className="revision-counter">
                {totalRevisions - currentRevisionIndex} of {totalRevisions}
              </span>
              <button 
                className="nav-button" 
                onClick={handleNext}
                disabled={isLastRevision}
                title="Next Revision"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
          <button className="revision-history-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        
        <div className="revision-history-modal-content">
          <div className="revision-history-item">
            <div className="revision-history-header">
              <div className="revision-number">
                <FileText size={16} />
                <span>Revision {currentRevision.revisionNumber}</span>
              </div>
              <div className="revision-date">
                <Calendar size={14} />
                <span>{formatDate(currentRevision.revisionDate)}</span>
              </div>
            </div>

            <div className="revision-history-details">
              <div className="revision-detail-row">
                <div className="revision-detail-item">
                  <User size={14} />
                  <span className="detail-label">Proposed by:</span>
                  <span className="detail-value">{currentRevision.revisionProposedBy}</span>
                </div>
                <div className="revision-detail-item">
                  <User size={14} />
                  <span className="detail-label">Approved by:</span>
                  <span className="detail-value">{currentRevision.revisionApprovedBy}</span>
                </div>
              </div>

              <div className="revision-reason">
                <span className="reason-label">Reason:</span>
                <span className="reason-text">{currentRevision.reasonForRevision}</span>
              </div>

              <div className="revision-changes">
                <div className="changes-section">
                  <h4>Previous Values</h4>
                  <div className="changes-grid">
                    <div className="change-item">
                      <span className="change-label">SAC Code:</span>
                      <span className="change-value">{currentRevision.previousData.sacCode}</span>
                    </div>
                    <div className="change-item">
                      <span className="change-label">Description:</span>
                      <span className="change-value">{currentRevision.previousData.description}</span>
                    </div>
                    <div className="change-item">
                      <span className="change-label">GST Rate:</span>
                      <span className="change-value">{currentRevision.previousData.gstRate}%</span>
                    </div>
                    <div className="change-item">
                      <span className="change-label">IGST Rate:</span>
                      <span className="change-value">{currentRevision.previousData.igstRate}%</span>
                    </div>
                    <div className="change-item">
                      <span className="change-label">SGST Rate:</span>
                      <span className="change-value">{currentRevision.previousData.sgstRate}%</span>
                    </div>
                    <div className="change-item">
                      <span className="change-label">CGST Rate:</span>
                      <span className="change-value">{currentRevision.previousData.cgstRate}%</span>
                    </div>
                    <div className="change-item">
                      <span className="change-label">UTGST Rate:</span>
                      <span className="change-value">{currentRevision.previousData.utgstRate}%</span>
                    </div>
                  </div>
                </div>

                <div className="changes-section">
                  <h4>New Values</h4>
                  <div className="changes-grid">
                    <div className="change-item">
                      <span className="change-label">SAC Code:</span>
                      <span className="change-value new">{currentRevision.newData.sacCode}</span>
                    </div>
                    <div className="change-item">
                      <span className="change-label">Description:</span>
                      <span className="change-value new">{currentRevision.newData.description}</span>
                    </div>
                    <div className="change-item">
                      <span className="change-label">GST Rate:</span>
                      <span className="change-value new">{currentRevision.newData.gstRate}%</span>
                    </div>
                    <div className="change-item">
                      <span className="change-label">IGST Rate:</span>
                      <span className="change-value new">{currentRevision.newData.igstRate}%</span>
                    </div>
                    <div className="change-item">
                      <span className="change-label">SGST Rate:</span>
                      <span className="change-value new">{currentRevision.newData.sgstRate}%</span>
                    </div>
                    <div className="change-item">
                      <span className="change-label">CGST Rate:</span>
                      <span className="change-value new">{currentRevision.newData.cgstRate}%</span>
                    </div>
                    <div className="change-item">
                      <span className="change-label">UTGST Rate:</span>
                      <span className="change-value new">{currentRevision.newData.utgstRate}%</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="revision-timestamp">
                <Clock size={14} />
                <span>Created: {formatDateTime(currentRevision.createdAt)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="revision-history-modal-actions">
          <button className="revision-history-btn-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default RevisionHistoryModal
